import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { FadeUp, Eyebrow } from "./motion";
import { useContent } from "../lib/content";
import { scrollToId } from "../lib/site";

export default function FAQ() {
  const { faqs } = useContent();
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <FadeUp className="lg:col-span-5">
          <Eyebrow className="mb-4">FAQ</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12] text-slate-900">
            Questions about curtains in Dubai — <span className="italic text-reef">answered.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            Still unsure about something? Book a free visit and our specialists will walk you
            through fabrics, pricing and timelines at your doorstep.
          </p>
          <button
            data-testid="faq-book-btn"
            onClick={() => scrollToId("book")}
            className="mt-8 px-7 py-4 rounded-full bg-reef text-sand-bg font-mono text-xs tracking-[0.18em] uppercase hover:bg-reef-dark transition-colors duration-300"
          >
            Book a Free Visit
          </button>
        </FadeUp>

        <div className="lg:col-span-7 space-y-3">
          {faqs.map((f, i) => (
            <FadeUp key={i} delay={i * 0.05}>
              <div
                data-testid={`faq-item-${i}`}
                className={`bg-white border rounded-2xl transition-colors duration-300 ${
                  open === i ? "border-reef/40" : "border-sand-border"
                }`}
              >
                <button
                  data-testid={`faq-question-${i}`}
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <span className="font-serif text-lg text-slate-900">{f.q}</span>
                  <motion.span
                    animate={{ rotate: open === i ? 45 : 0 }}
                    transition={{ duration: 0.25 }}
                    className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      open === i ? "bg-reef text-sand-bg" : "bg-reef-light text-reef"
                    }`}
                  >
                    <Plus className="w-4 h-4" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p data-testid={`faq-answer-${i}`} className="px-6 pb-6 text-sm sm:text-base text-slate-600 leading-relaxed">
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
